import { Combobox, ComboboxItem, ComboboxList, ComboboxProvider, useComboboxStore } from "@ariakit/react";
import * as Popover from "@radix-ui/react-popover";
import classNames from "classnames";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import React, { CSSProperties, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";

import { useBreakpoint } from "~/contexts/breakpoint";
import Logger from "~/services/logger/logger";
import crossCountryUtils from "~/utils/cross-country-utils";
import woosmapUtils, { WooosmapGeometry } from "~/utils/woosmap-utils";

import Button from "../common/button";
import Icon from "../common/icon";
import comboboxStyles from "../form/controlled-combobox.module.scss";
import styles from "./localities-autocomplete.module.scss";
import autocompleteStrategies from "./localities-autocomplete-strategies";

interface Suggestion {
  description: string;
  public_id: string;
}

type LocalitiesAutocompleteProps = {
  provider?: "woosmap";
  label: string;
  placeholder?: string;
  resultType?: string;
  onSelectCoordinates?: (selection: WooosmapGeometry) => void;
  onSelectCity?: (selection: { city: string; postal_code: string }) => void;
  onSelectLocality?: (selection: Omit<EVA.Core.AddressDto, "ID">) => void;
  isOptional?: boolean;
  onEditManually?: () => void;
  isFormVisible?: boolean;
  externalLoading?: boolean;
  informativeMessage?: string;
  showSearchIcon?: boolean;
  error?: string;
  displayValue?: string;
};

const LocalitiesAutocomplete: React.FC<LocalitiesAutocompleteProps> = ({
  provider = "woosmap",
  label,
  placeholder,
  resultType,
  onSelectCoordinates,
  onSelectCity,
  onSelectLocality,
  isOptional,
  onEditManually,
  isFormVisible = true,
  externalLoading,
  informativeMessage,
  showSearchIcon,
  error,
  displayValue,
}) => {
  const t = useTranslations();
  const router = useRouter();
  const id = useId();
  const language = crossCountryUtils.getCurrentLanguageCode(router);
  const currentCountryID = crossCountryUtils.getCurrentCountryCode(router);

  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [selected, setSelected] = useState<Suggestion | null>(null);
  const [selectedOption, setSelectedOption] = useState<boolean>(false);
  const [isInputFocused, setInputFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // meant for avoiding empty state while loading the first results after 3 charachters
  const [firstLoad, setFirstLoad] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);

  const breakpoint = useBreakpoint();

  const commonParams = useMemo(() => {
    return {
      components: `country:${currentCountryID}`,
      language: language,
      types: resultType ?? "address",
    };
  }, [currentCountryID, language, resultType]);

  const fetchSuggestions = useCallback(
    async (query: string) => {
      if (query.length < 3) {
        setFirstLoad(true);
        setSuggestions([]);
        return;
      }

      try {
        const response = await autocompleteStrategies[provider].fetchSuggestion(commonParams, query);
        setSuggestions(response.data.localities || []);
      } catch (error) {
        Logger.instance.error("Error fetching suggestions:", error);
      } finally {
        setIsLoading(false);
        setFirstLoad(false);
      }
    },
    [commonParams, provider]
  );

  // Focus management when the form is completeley expanded after entering the address
  useEffect(() => {
    if (isFormVisible) {
      inputRef.current?.focus();
    }
  }, [isFormVisible]);

  const fetchPlaceDetails = async (publicId: string) => {
    try {
      const response = await autocompleteStrategies[provider].fetchDetails(publicId);
      const placeDetails = response.data.result;

      if (placeDetails) {
        Logger.instance.info("place details obtained", placeDetails);
        if (placeDetails.geometry && onSelectCoordinates) {
          onSelectCoordinates(placeDetails.geometry);
        }
        if (onSelectCity) {
          let selectedCity = {
            city: "",
            postal_code: "",
          };
          if (
            (placeDetails.types?.includes("postal_code") || placeDetails.types?.includes("postal_codes")) &&
            placeDetails.name
          ) {
            selectedCity.postal_code = placeDetails.name;
          } else {
            const postalCodes = placeDetails.address_components.find(
              (comp: { types: string | string[] }) =>
                comp.types.includes("postal_code") || comp.types.includes("postal_codes")
            )?.short_name;
            if (Array.isArray(postalCodes)) {
              selectedCity.postal_code = postalCodes[0];
            } else {
              selectedCity.postal_code = postalCodes || "";
            }
          }
          if (placeDetails.types?.includes("locality") && placeDetails.name) {
            selectedCity.city = placeDetails.name;
          } else {
            selectedCity.city =
              placeDetails.address_components.find(
                (comp: { types: string | string[] }) =>
                  comp.types.includes("locality") || comp.types.includes("postal_town")
              )?.short_name || "";
          }
          onSelectCity(selectedCity);
        }
        if (onSelectLocality) {
          const address = woosmapUtils.mapSuggestionToAddressDto(placeDetails, currentCountryID);
          onSelectLocality(address);
        }
      }
    } catch (error) {
      Logger.instance.error("Error fetching place details:", error);
    }
  };

  // debounced fetchSuggestions with cleanup
  useEffect(() => {
    if (query.length >= 3) {
      setIsLoading(true);
    }
    const debounce = setTimeout(() => {
      fetchSuggestions(query);
    }, 300);

    return () => {
      clearTimeout(debounce);
    };
  }, [query, fetchSuggestions]);

  const loadingState = useMemo(() => {
    return isLoading || externalLoading;
  }, [isLoading, externalLoading]);

  const handleSelection = (suggestion: Suggestion) => {
    setSelectedOption(true);
    setSelected(suggestion);
    if (suggestion) {
      setQuery(suggestion.description);
      fetchPlaceDetails(suggestion.public_id);
    }
  };

  //the width of the label that will be used to create a transparent label is kept track of
  const refLabel = useRef<HTMLLabelElement>(null);
  const [widthLabel, setWidthLabel] = useState<number | undefined>();
  const [open, setOpen] = useState<boolean>(false);
  const [panelWidth, setPanelWidth] = useState<number>(0);
  const [isMouseClick, setIsMouseClick] = useState<boolean>(false);
  const popoverAnchor = useRef<HTMLDivElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);
  const comboboxOption = useRef<HTMLDivElement>(null);
  const styleLabelLine = {
    "--label-line-width": `calc(100% - ${widthLabel}px - var(--label-external-padding-horizontal))`,
  } as CSSProperties;

  const combobox = useComboboxStore({
    open,
    setOpen,
    defaultValue: displayValue ?? (selected ? selected.description : query),
    animated: true, // smoother opening/closing
  });

  useEffect(() => {
    // CommonUtils.isWindows() && comboboxPanelClassnames.push(Constants.SCROLLBAR_CLASSNAME);
    setWidthLabel(refLabel.current?.offsetWidth);
  }, [isOptional]);

  // combobox panel width
  useEffect(() => {
    const updateWidth = () => {
      if (popoverAnchor.current) {
        setPanelWidth(popoverAnchor.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => {
      window.removeEventListener("resize", updateWidth);
    };
  }, []);

  const formatText = (suggestion: string) => {
    const boldText = suggestion.split(", ");
    return (
      <>
        <span className="localities-item-query">{boldText[0]}</span>, {boldText.slice(1).join(", ")}
      </>
    );
  };

  return (
    <div className={classNames(styles.autocompleteContainer, !isFormVisible ? styles.spanCol : null)}>
      <div
        className={classNames(
          comboboxStyles.container,
          isInputFocused ? comboboxStyles.containerInFocus : null,
          error ? comboboxStyles.containerWithErrors : null
        )}
      >
        <div className={comboboxStyles.wrapperLabelField}>
          <label
            htmlFor={id}
            ref={refLabel}
            style={styleLabelLine}
            className={comboboxStyles.labelField}
            onMouseDown={(e) => e.preventDefault()}
          >
            {label}
            {isOptional ? (
              ""
            ) : (
              <span className={comboboxStyles.mandatory} aria-hidden>
                {t("generic.form.mandatory_short")}
              </span>
            )}
          </label>
        </div>
        <div
          className={classNames(comboboxStyles.wrapperInputField)}
          onFocus={() => setInputFocused(true)}
          onBlur={(e) => {
            // if relatedTarget is not defined or not inside wrapperInputField
            if (!e.relatedTarget || !e.currentTarget.contains(e.relatedTarget)) {
              setInputFocused(false);
            }
          }}
        >
          <Popover.Root open={open} onOpenChange={setOpen}>
            <ComboboxProvider open={open} setOpen={setOpen}>
              <Popover.Anchor asChild>
                <div className={comboboxStyles.popoverAnchor} ref={popoverAnchor}>
                  {showSearchIcon && (
                    <Icon name="search" width={16} height={16} className={styles.inputIcon} aria-hidden />
                  )}
                  <Combobox
                    ref={inputRef}
                    id={id}
                    className={comboboxStyles.inputField}
                    placeholder={placeholder || "Search a location..."}
                    onChange={(event) => setQuery(event.target.value)}
                    onMouseDown={() => setIsMouseClick(true)}
                    onBlur={(event) => {
                      if (!selectedOption && isMouseClick && breakpoint != "mobile") {
                        const address = event.target.value;
                        if (onSelectLocality && !!address) {
                          onSelectLocality({
                            Address1: address,
                          });
                        }
                      }
                      setSelectedOption(false);
                    }}
                    store={combobox}
                  />
                  {loadingState && <span className={styles.loader} />}
                </div>
              </Popover.Anchor>

              <Popover.Content
                className={comboboxStyles.comboboxContainer}
                side="bottom"
                align="start"
                onOpenAutoFocus={(event) => event.preventDefault()}
                onInteractOutside={(event) => {
                  const target = event.target as Element | null;
                  // in case of combobox/listbox interaction the panel must not close
                  const shouldPreventModalClose =
                    target === inputRef.current || (target && listboxRef.current?.contains(target));
                  if (shouldPreventModalClose) {
                    event.preventDefault();
                  }
                }}
              >
                <>
                  <ComboboxList
                    ref={listboxRef}
                    role="listbox"
                    className={classNames(comboboxStyles.comboboxOptionsPanel, comboboxStyles.localitiesPanel)}
                    style={{ width: `${panelWidth}px` }}
                    store={combobox}
                  >
                    {query.length < 3 || firstLoad ? (
                      <ComboboxItem
                        disabled
                        value={undefined}
                        className={classNames(comboboxStyles.comboboxOption, comboboxStyles.noResult)}
                      >
                        {t("generic.combobox_min_characters")}
                      </ComboboxItem>
                    ) : suggestions.length === 0 && !loadingState ? (
                      <ComboboxItem
                        disabled
                        value={undefined}
                        className={classNames(comboboxStyles.comboboxOption, comboboxStyles.noResult)}
                      >
                        {t("generic.combobox_no_results")}
                      </ComboboxItem>
                    ) : (
                      suggestions.map((suggestion) => (
                        <ComboboxItem
                          role="option"
                          ref={comboboxOption}
                          key={suggestion.public_id}
                          className={classNames(comboboxStyles.comboboxOption, "localities-item")}
                          value={suggestion.description.substring(0, suggestion.description.indexOf(","))}
                          onClick={() => {
                            handleSelection(suggestion);
                            setOpen(false);
                          }}
                          store={combobox}
                          aria-selected={selected?.public_id == suggestion.public_id}
                        >
                          {formatText(suggestion.description)}
                        </ComboboxItem>
                      ))
                    )}
                  </ComboboxList>
                </>
              </Popover.Content>
            </ComboboxProvider>
          </Popover.Root>
        </div>
      </div>
      {error ? (
        <p className={comboboxStyles.errorMessage}>{error}</p>
      ) : informativeMessage ? (
        <p className={comboboxStyles.informativeMessage}>{informativeMessage}</p>
      ) : (
        <></>
      )}
      {!isFormVisible && (
        <Button
          lookAsLink
          onClick={onEditManually}
          onKeyDown={(event) => {
            if (!selectedOption) {
              if (onSelectLocality && !!query) {
                onSelectLocality({
                  Address1: query,
                });
              }
            }
          }}
          className={styles.manuallyBtn}
        >
          {t("generic.address.entermanually")}
        </Button>
      )}
    </div>
  );
};

export default LocalitiesAutocomplete;
